import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Table, Badge } from "reactstrap";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import Swal from "sweetalert2";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import ServerPagination from "components/Common/serverPagination";
import Loader from "pages/Separate/Loader";
import AccessDeniedPopup from "routes/AccessDeniedPopup";
import configure from "configure";

import "../../../assets/css/Sales/Lead.css";

const formatValue = (value, fallback = "-") =>
    value === null || value === undefined || value === "" ? fallback : value;

const getPersonName = (personDetailsRaw) => {
    try {
        const parsed =
            typeof personDetailsRaw === "string"
                ? JSON.parse(personDetailsRaw)
                : personDetailsRaw;
        return Array.isArray(parsed) ? parsed[0]?.person_name : null;
    } catch {
        return null;
    }
};

// embedded: true when rendered inline as the "Deleted Leads" tab of
// pages/Sales/Lead/index.js -- skips the page chrome (breadcrumb/back link)
// that only makes sense when this is its own routed page
// (sales/deleted-leads).
const DeletedLeads = ({ embedded = false, tabSwitcher = null }) => {
    if (!embedded) {
        document.title = "Deleted Leads | Zithas Technologies";
    }

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(20);
    const [keyword, setKeyword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState({ india: [], uk: [] });
    const [modal_Access, setAccessDenied] = useState(false);

    const fetch = async (page = 1, search = "") => {
        setIsLoading(true);
        try {
            const response = await axios({
                method: "post",
                url: `${configure.nodeApi}/deleted_leads`,
                data: {
                    user_id: localStorage.getItem("auth_id"),
                    page,
                    limit,
                    keyword: search,
                },
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            setData(response?.data?.data || []);
            setTotal(response?.data?.total || 0);
            setCurrentPage(page);
        } catch (err) {
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetch(1, "");

        const formData = new FormData();
        formData.append("user_id", localStorage.getItem("auth_id"));
        formData.append("module_id", 17);

        axios({
            method: "post",
            url: `${configure.nodeApi}/user_permission`,
            data: formData,
            headers: {
                Accept: "application/json",
                "Content-Type": "multipart/form-data",
            },
        })
            .then((res) => {
                setPermissions({
                    india: res.data.data.india,
                    uk: res.data.data.uk,
                });
            })
            .catch((err) => console.log(err));
    }, []);

    let searchTimeout;
    const handleSearch = (value) => {
        setKeyword(value);
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => fetch(1, value), 500);
    };

    const onRestore = (lead) => {
        if (!(permissions.india[5] == 1 || permissions.uk[5] == 1)) {
            setAccessDenied(true);
            return;
        }

        swal({
            title: "Are you sure?",
            text: `You want to restore "${lead.company || "this lead"}" back to the Lead list?`,
            icon: "warning",
            buttons: true,
        }).then(async (willRestore) => {
            if (!willRestore) return;
            setIsLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: `${configure.nodeApi}/restore_lead`,
                    data: {
                        id: lead.id,
                        user_id: localStorage.getItem("auth_id"),
                    },
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                });
                if (response?.data?.success) {
                    Swal.fire({
                        icon: "success",
                        text: "Lead restored successfully",
                    });
                    fetch(currentPage, keyword);
                }
            } catch (err) {
                console.log(err);
                Swal.fire({
                    icon: "error",
                    text: "Failed to restore lead",
                });
            } finally {
                setIsLoading(false);
            }
        });
    };

    const content = (
        <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                {tabSwitcher}
                <div className="search-box d-inline-block">
                    <div className="position-relative">
                        <input
                            type="text"
                            className="form-control rounded"
                            placeholder="Search deleted leads..."
                            value={keyword}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        <i className="bx bx-search-alt search-icon"></i>
                    </div>
                </div>
            </div>

            <div className="responsive-table">
                <Table className="table table-bordered align-middle">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Company</th>
                            <th>Contact</th>
                            <th>Assigned</th>
                            <th>Source</th>
                            <th>Status</th>
                            <th>Deleted By</th>
                            <th>Deleted On</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={9} className="text-center py-4 text-muted">
                                    No deleted leads found.
                                </td>
                            </tr>
                        )}
                        {data.map((lead) => (
                            <tr key={lead.id} className="lead-row">
                                <td>{lead.id}</td>
                                <td>
                                    <div className="lead-company-name">
                                        {formatValue(lead.company, "Unnamed Company")}
                                    </div>
                                </td>
                                <td>
                                    <div>{formatValue(getPersonName(lead.person_details), lead.email)}</div>
                                    <div className="text-muted lead-meta-line">
                                        {formatValue(lead.phonenumber)}
                                    </div>
                                </td>
                                <td>{formatValue(lead.asigned)}</td>
                                <td>{formatValue(lead.source)}</td>
                                <td>
                                    <Badge className={`${lead.status_color} lead-status-badge`} pill>
                                        {formatValue(lead.status_name, "Unknown")}
                                    </Badge>
                                </td>
                                <td>{formatValue(lead.deleted_by)}</td>
                                <td style={{width : "15vw"}}>
                                    {lead.deleted_at
                                        ? moment(lead.deleted_at).format("DD MMM YYYY, hh:mm A")
                                        : "-"}
                                </td>
                                <td className="text-center">
                                    <button
                                        type="button"
                                        title="restore lead"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => onRestore(lead)}
                                    >
                                        <i className="bx bx-undo me-1"></i>
                                        Restore
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <ServerPagination
                currentPage={currentPage}
                pageSize={limit}
                totalItems={total}
                onPageChange={(newPage) => fetch(newPage, keyword)}
            />
        </>
    );

    if (embedded) {
        return (
            <React.Fragment>
                {isLoading && <Loader />}
                {content}
                <AccessDeniedPopup modal_Access={modal_Access} setAccessDenied={setAccessDenied} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {isLoading && <Loader />}
            <div className="page-content" id="lead-listing">
                <Container fluid>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <Breadcrumbs title="Sales" breadcrumbItem="Deleted Leads" />
                        <Link
                            to={`${configure.appUrl}sales/lead`}
                            className="btn btn-outline-secondary btn-label"
                        >
                            <i className="fas fa-arrow-left me-2 icon-size label-icon"></i>
                            Back to Leads
                        </Link>
                    </div>

                    <div className="bg-white p-3 rounded shadow-sm">{content}</div>
                </Container>
            </div>

            <AccessDeniedPopup modal_Access={modal_Access} setAccessDenied={setAccessDenied} />
        </React.Fragment>
    );
};

export default DeletedLeads;
